import { FormattedMessage, useIntl } from "react-intl";

// Components
import { HeaderStep } from "components/HeaderStep";
import { ErrorBoundary } from "react-error-boundary";
import { BriefFacets } from "./components/BriefFacets";
import { BriefProfile } from "./components/BriefProfile";
import GenericError from "components/Errors/GenericError";
import { BriefPosition } from "./components/BriefPosition";
import NavButton from "components/HeaderNavigation/NavButton";
import { Alert, Backdrop, Box, Button, Grid, IconButton, Typography } from "@mui/material";
import { Header, HeaderInner, Body } from "components/Layout/LayoutContent";
import { CircledElement, VerticalLine } from "pages/ProjectDashboard/styled-components";
import CustomIcon from "components/CustomIcon";
import Modal from "components/Modal";
import CloseIcon from "@mui/icons-material/Close";

// Hooks
import useBrief from "./useBrief";

// Utils
import { isEmpty } from "lodash";

// Data
import { briefHeaderSteps } from "./Brief.data";
import icons from "enums/icons";

// Styles
import briefStyles from "./Brief.styles";
import { ClearFilterButton } from "components/Buttons/ClearFilterButton";

const Brief = () => {
    const classes = briefStyles();
    const intl = useIntl();

    const {
        isModalOpen,
        error,
        briefData,
        chartStatus,
        shouldDisableFiltersButton,
        handleConfirmationModal,
        handleGoToDashboard,
        handleValidateFields,
        handleClearError,
        handleSetBriefData,
        handleResetFilters,
    } = useBrief();

    return (
        <ErrorBoundary FallbackComponent={GenericError}>
            {isModalOpen && (
                <Backdrop
                    component="div"
                    open={isModalOpen}
                    sx={{ backgroundColor: "transparent", backdropFilter: "blur(10px)", zIndex: 2000 }}
                >
                    <Modal open={isModalOpen} sx={{ zIndex: 2000, boxShadow: "1px 4px 12px 0 #000" }}>
                        <Box
                            sx={{
                                width: "100%",
                                maxWidth: 615,
                                p: 2,
                                borderRadius: 0.8,
                                backgroundColor: "background.blue",
                            }}
                        >
                            <Box>
                                <Box sx={{ display: "flex", marginBottom: 1.5, justifyContent: "space-between" }}>
                                    <Box display="flex" alignItems="center">
                                        <CustomIcon
                                            style={{ fontSize: 24, alignSelf: "center", marginRight: "8px" }}
                                            icon={icons.circledExclamationSign}
                                        />
                                        <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                                            {intl.formatMessage({ id: "brief.modal.messageTitle" })}
                                        </Typography>
                                    </Box>

                                    <IconButton aria-label="close" size="small" onClick={handleConfirmationModal}>
                                        <CloseIcon />
                                    </IconButton>
                                </Box>

                                <Typography paragraph variant="body2">
                                    {intl.formatMessage({ id: "brief.modal.messageBody" })}
                                </Typography>
                            </Box>

                            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                                <Button
                                    variant="contained"
                                    color="error"
                                    onClick={handleGoToDashboard}
                                    sx={{ marginRight: 1, fontSize: 12 }}
                                    startIcon={<CustomIcon icon={icons.chevronLeftThin} />}
                                >
                                    <Typography sx={{ fontSize: 16 }}>
                                        {intl.formatMessage({ id: "button.backToDashboard" })}
                                    </Typography>
                                </Button>

                                <Button variant="outlined" onClick={handleConfirmationModal}>
                                    <Typography sx={{ fontSize: 16 }}>
                                        {intl.formatMessage({ id: "button.continueEditing" })}
                                    </Typography>
                                </Button>
                            </Box>
                        </Box>
                    </Modal>
                </Backdrop>
            )}
            <Header isSticky backgroundColor="primary">
                <HeaderInner>
                    <Typography className={classes.headerTitle}>Create Brief</Typography>
                    <Box className={classes.headerStepsWrapper}>
                        {briefHeaderSteps.map((el, i) => {
                            return <HeaderStep key={i} label={el.label} innerLabel={`${i + 1}`} sx={{ ml: 2 }} />;
                        })}
                    </Box>
                    <Box>
                        <NavButton
                            isBackButton
                            onClick={handleConfirmationModal}
                            translationId="button.backToDashboard"
                        />
                        <NavButton onClick={() => handleValidateFields(briefData)} translationId="button.reviewBrief" />
                    </Box>
                </HeaderInner>
            </Header>
            <Body>
                <Box sx={{ position: "relative", p: 3, pb: 8 }}>
                    {error.id && (
                        <Alert
                            iconMapping={{ warning: <CustomIcon icon={icons.errorTriangle} /> }}
                            variant="filled"
                            severity="warning"
                            sx={{ mb: 3 }}
                            action={
                                <Button
                                    color="secondary"
                                    onClick={handleClearError}
                                    variant="outlined"
                                    size="small"
                                    sx={{ alignSelf: "flex-start" }}
                                >
                                    <FormattedMessage id="setup-company.vcp.error.action.label" />
                                </Button>
                            }
                        >
                            {error.message}
                        </Alert>
                    )}

                    <Typography className={classes.intro}>
                        {intl.formatMessage({ id: "brief.createBrief.explanation" })}
                    </Typography>

                    <Box display="flex" justifyContent="flex-end">
                        <ClearFilterButton
                            onClick={handleResetFilters}
                            disabled={shouldDisableFiltersButton}
                            endIcon={<CustomIcon icon={icons.circledTimesHollowed} />}
                        />
                    </Box>

                    {/* Position */}
                    <Grid container sx={{ mb: 4 }}>
                        <Grid item lg={12} display="flex" width="100%">
                            <Box justifyContent="center">
                                <CircledElement color="primary" size={45}>
                                    1
                                </CircledElement>
                                <VerticalLine />
                            </Box>
                            <Box width="100%" ml={1}>
                                {!isEmpty(briefData) && (
                                    <BriefPosition
                                        briefData={briefData}
                                        setBriefData={handleSetBriefData}
                                        isLoading={chartStatus !== "done"}
                                        hasError={error.id === "STEP1"}
                                    />
                                )}
                            </Box>
                        </Grid>
                    </Grid>

                    {/* Profile */}
                    <Grid container>
                        <Grid item lg={12} display="flex" width="100%">
                            <Box justifyContent="center">
                                <CircledElement color="primary" size={45}>
                                    2
                                </CircledElement>
                                <VerticalLine />
                            </Box>
                            <Box className={classes.profileWrapper}>
                                <BriefProfile
                                    briefData={briefData}
                                    setBriefData={handleSetBriefData}
                                    isLoading={chartStatus !== "done"}
                                    chart1HasError={error.chart1}
                                    chart2HasError={error.chart2}
                                />
                            </Box>
                        </Grid>
                    </Grid>

                    {/* Facets */}
                    <Grid container>
                        <Grid item lg={12} display="flex" width="100%">
                            <Box justifyContent="center">
                                <CircledElement color="primary" size={45}>
                                    3
                                </CircledElement>
                            </Box>
                            <Box ml={1}>
                                <BriefFacets
                                    briefData={briefData}
                                    setBriefData={handleSetBriefData}
                                    hasError={error.id === "STEP3"}
                                    isLoading={chartStatus !== "done"}
                                />
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
            </Body>
        </ErrorBoundary>
    );
};

export default Brief;
